import Link from "next/link";
import { REPORT_CONTENT } from "@/lib/reports-content";
import OfferImpression from "@/components/OfferImpression";
import type { SEOIntent, SEOReportPlanId } from "@/lib/seo-intents";

const REPORT_SLUG_BY_PLAN: Record<SEOReportPlanId, string> = {
  brihat_kundali: "brihat-kundali",
  marriage_report: "marriage-report",
  career_report: "career-report",
  annual_forecast: "annual-forecast",
  sade_sati: "sade-sati",
  pitra_dosh: "pitra-dosh",
  mahadasha_forecast: "mahadasha-forecast",
  muhurat_report: "muhurat-report",
  dosha_remedy_plan: "dosha-remedy-plan",
};

export default function CalculatorReportBridge({
  planId,
  source,
  intent,
  title,
  body,
  cta = "Get chart-backed report",
  language = "en",
}: {
  planId: SEOReportPlanId;
  source: string;
  intent: SEOIntent;
  title: string;
  body: string;
  cta?: string;
  language?: "en" | "hi";
}) {
  const reportSlug = REPORT_SLUG_BY_PLAN[planId];
  const report = REPORT_CONTENT[reportSlug];
  const params = new URLSearchParams({
    intent,
    source,
    next_report: planId,
  });
  const href = `${language === "hi" ? "/hi/free-kundali/" : "/free-kundali/"}#${params.toString()}`;

  return (
    <section className="mt-6 rounded-lg border border-[var(--ish-border)] bg-[var(--ish-bg-card)] p-5 text-center shadow-sm">
      {/* Render-nothing telemetry island: makes this priced report CTA's reach
          measurable (offer_seen), fired once. Without it muhurat/dasha/sade-sati
          read 0% reach despite the CTA being on screen (ADR-0025, rule 46). */}
      <OfferImpression
        planId={planId}
        sourcePath={source}
        intent={intent}
        placement={`${source}_report_bridge`}
      />
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--ish-accent)]">
        {language === "hi" ? "Agla Step" : "Next Step"}
      </p>
      <h2 className="mt-2 font-serif text-[22px] leading-tight text-[var(--ish-dark)]">{title}</h2>
      <p className="mx-auto mt-2 max-w-xl text-[13px] leading-6 text-[var(--ish-text-muted)]">
        {body}
      </p>
      <Link
        href={href}
        className="mt-4 inline-flex min-h-11 items-center justify-center rounded-md bg-[var(--ish-accent)] px-5 text-[14px] font-semibold text-white"
      >
        {cta} · ₹{report.price_inr.toLocaleString("en-IN")}
      </Link>
    </section>
  );
}
